<template>
  <CTabs variant="tabs" class="nav-tabs-boxed">
    <CTab title="Campanhas Comerciais">
      <div class="campanha-listagem">
        <Loading :isActive="loading" />

        <CampanhaFormModal :item="item" :close="closeEdit" />

        <div class="campanha-listagem__header">
          <h4 class="campanha-listagem__title">Campanhas Comerciais</h4>
          <CButton color="primary" @click="openCreate">
            <CIcon name="cil-plus" size="sm" class="mr-1" />
            Nova Campanha
          </CButton>
        </div>

        <CampanhaStatsRow
          :ativas="estatisticas.ativas"
          :agendadas="estatisticas.agendadas"
          :encerrando="estatisticas.encerrando"
        />

        <div class="campanha-listagem__filtros">
          <CRow>
            <CCol md="4">
              <div class="filtro-search-wrapper">
                <CIcon name="cil-magnifying-glass" size="sm" class="filtro-search-icon" />
                <input
                  class="form-control filtro-search-input"
                  type="text"
                  v-model="filtros.termo"
                  placeholder="Buscar por descrição..."
                  @keyup.enter="loadCampanhas"
                />
              </div>
            </CCol>
            <CCol md="3">
              <select class="form-control" v-model="filtros.status" @change="loadCampanhas">
                <option value="">Todos os status</option>
                <option value="ATIVA">Ativas</option>
                <option value="AGENDADA">Agendadas</option>
                <option value="ENCERRADA">Encerradas</option>
              </select>
            </CCol>
            <CCol md="3">
              <datetime
                input-class="form-control"
                v-model="filtros.data"
                type="date"
                placeholder="Vigentes em"
                :phrases="{ ok: 'Confirmar', cancel: 'Fechar' }"
                value-zone="local"
              />
            </CCol>
            <CCol md="2">
              <CButton color="primary" block @click="loadCampanhas">
                <CIcon name="cil-magnifying-glass" size="sm" class="mr-1" />
                Buscar
              </CButton>
            </CCol>
          </CRow>
        </div>

        <div class="campanha-listagem__table-wrapper">
          <table class="campanha-listagem__table">
            <thead>
              <tr>
                <th class="col-id">ID</th>
                <th class="col-desc">Descrição</th>
                <th class="col-status">Status</th>
                <th class="col-data">Início</th>
                <th class="col-data">Término</th>
                <th class="col-tipo">Tipo</th>
                <th class="col-validacao">Validação</th>
                <th class="col-origem">Origem</th>
                <th class="col-canais">Canais</th>
                <th class="col-acoes"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="campanhas.length === 0">
                <td colspan="10" class="campanha-listagem__empty">
                  <CIcon name="cil-ban" size="xl" class="campanha-listagem__empty-icon" />
                  <p>Nenhuma campanha encontrada</p>
                </td>
              </tr>
              <tr
                v-for="campanha in campanhas"
                :key="campanha.id"
                class="campanha-listagem__row"
              >
                <td class="col-id">{{ campanha.id }}</td>
                <td class="col-desc">
                  <span class="campanha-listagem__desc">{{ campanha.descricao }}</span>
                </td>
                <td class="col-status">
                  <CampanhaStatusBadge :campanha="campanha" />
                </td>
                <td class="col-data">{{ campanha.dataInicio | date }}</td>
                <td class="col-data">{{ campanha.dataFim | date }}</td>
                <td class="col-tipo">
                  <span class="campanha-listagem__tipo-label">
                    {{ getTipoLabel(campanha.tipo) }}
                  </span>
                </td>
                <td class="col-validacao">
                  <span class="campanha-listagem__validacao-label">
                    {{ getValidacaoLabel(campanha.validacao) }}
                  </span>
                </td>
                <td class="col-origem">
                  <span v-if="campanha.erpId" class="origem-badge origem-badge--erp">ERP</span>
                  <span v-else class="origem-badge origem-badge--verso">Verso</span>
                </td>
                <td class="col-canais">
                  <div class="campanha-listagem__canais">
                    <span v-if="campanha.aplicab2b" class="canal-icon" title="B2B">
                      <fa-icon icon="shopping-basket" />
                    </span>
                    <span v-if="campanha.aplicacallcenter" class="canal-icon" title="Callcenter">
                      <fa-icon icon="headset" />
                    </span>
                    <span v-if="campanha.aplicafv" class="canal-icon" title="Força de Vendas">
                      <fa-icon icon="user-tie" />
                    </span>
                  </div>
                </td>
                <td class="actions">
                  <CButton
                    size="sm"
                    color="secondary"
                    @click="cloneCampanha(campanha)"
                    title="Duplicar"
                  >
                    <fa-icon icon="clone" />
                  </CButton>
                  <CButton
                    size="sm"
                    color="primary"
                    @click="editCampanha(campanha)"
                    title="Alterar"
                  >
                    <fa-icon icon="pencil-alt" />
                  </CButton>
                  <CButton
                    size="sm"
                    color="danger"
                    @click="deleteConfirm(campanha.id)"
                    title="Excluir"
                  >
                    <fa-icon icon="trash-alt" />
                  </CButton>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="campanha-listagem__pagination">
          <pagination
            :data="table.paginator"
            :limit="5"
            @pagination-change-page="loadCampanhas"
          />
        </div>
      </div>
    </CTab>
  </CTabs>
</template>

<script>
import { get, del } from "@/helpers/apiRequest";
import moment from "moment";
import clone from "just-clone";
import Loading from "@/components/Loading.vue";
import CampanhaStatusBadge from "../shared/CampanhaStatusBadge.vue";
import CampanhaStatsRow from "../shared/CampanhaStatsRow.vue";
import CampanhaFormModal from "./CampanhaFormPage.vue";
import { defaultCampanhaComercial, cloneCampanhaParaCriacao } from "../helpers/campanhaDefaults";
import { getValidacaoLabel, getTipoLabel } from "../helpers/campanhaLabels";

export default {
  name: "CampanhaListagem",
  components: {
    Loading,
    CampanhaStatusBadge,
    CampanhaStatsRow,
    CampanhaFormModal,
  },
  data() {
    return {
      loading: false,
      item: defaultCampanhaComercial(),
      campanhas: [],
      estatisticas: {
        ativas: 0,
        agendadas: 0,
        encerrando: 0,
      },
      filtros: {
        termo: "",
        status: "",
        data: "",
      },
      table: {
        paginator: {},
      },
    };
  },
  methods: {
    getValidacaoLabel,
    getTipoLabel,
    loadCampanhas(page = 1) {
      this.loading = true;
      const date = this.getDate();
      const status = this.filtros.status || "";

      get(
        `/admin/campanhas-comerciais?page=${page}&termo=${this.filtros.termo}&data=${date}&status=${status}`
      )
        .then((json) => {
          this.campanhas = json.itens;
          this.table.paginator = json.paginator;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadEstatisticas() {
      get("/admin/campanhas-comerciais/estatisticas").then((json) => {
        this.estatisticas = json.data;
      });
    },
    openCreate() {
      this.item = defaultCampanhaComercial();
      this.$modal.show("campanhaComercialEdit");
    },
    editCampanha(campanha) {
      this.loading = true;
      get(`/admin/campanhas-comerciais/${campanha.id}`)
        .then((json) => {
          this.item = clone(json);
          this.$modal.show("campanhaComercialEdit");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    cloneCampanha(campanha) {
      this.loading = true;
      get(`/admin/campanhas-comerciais/${campanha.id}`)
        .then((json) => {
          this.item = cloneCampanhaParaCriacao(clone(json));
          this.$modal.show("campanhaComercialEdit");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    closeEdit() {
      this.$modal.hide("campanhaComercialEdit");
      this.loadCampanhas();
      this.loadEstatisticas();
    },
    deleteConfirm(id) {
      this.$swal({
        title: "Confirma Remover?",
        text: "Essa ação não poderá ser desfeita",
        showCancelButton: true,
        icon: "warning",
        confirmButtonText: "Excluir",
        cancelButtonText: "Cancelar",
      })
        .then((res) => {
          if (res.value) {
            this.loading = true;
            del(`/admin/campanhas-comerciais/${id}`)
              .then(() => {
                this.loadCampanhas();
                this.loadEstatisticas();
              })
              .catch(() => {
                this.$swal({
                  title: "Falha ao remover",
                  icon: "error",
                });
              })
              .finally(() => {
                this.loading = false;
              });
          }
        })
        .catch(() => {});
    },
    getDate() {
      if (this.filtros.data) {
        return moment(String(this.filtros.data)).format("YYYY-MM-DD");
      }
      return "";
    },
  },
  beforeMount() {
    this.loadCampanhas();
    this.loadEstatisticas();
  },
};
</script>

<style scoped>
/* ===== HEADER ===== */
.campanha-listagem__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.campanha-listagem__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #2c2c2c;
}

/* ===== FILTROS ===== */
.campanha-listagem__filtros {
  margin-bottom: 20px;
  padding: 16px;
  background: #f9fafb;
  border: 1px solid #e4e7eb;
  border-radius: 10px;
}

.campanha-listagem__filtros .form-control {
  font-size: 13px;
  border-radius: 6px;
  border: 1px solid #dddce9;
}

.campanha-listagem__filtros .form-control:focus {
  border-color: #3a3e81;
  box-shadow: 0 0 0 2px rgba(58, 62, 129, 0.1);
}

.filtro-search-wrapper {
  position: relative;
}

.filtro-search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #98a2b3;
  z-index: 1;
}

.filtro-search-input {
  padding-left: 36px !important;
}

/* ===== TABLE ===== */
.campanha-listagem__table-wrapper {
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  overflow: clip;
  background: #fff;
  margin-bottom: 20px;
}

.campanha-listagem__table {
  width: 100%;
  border-collapse: collapse;
}

.campanha-listagem__table thead tr {
  background: #f9fafb;
  border-bottom: 1px solid #e4e7eb;
}

.campanha-listagem__table th {
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  padding: 12px 12px;
  white-space: nowrap;
}

.campanha-listagem__table td {
  padding: 12px 12px;
  border-bottom: 1px solid #f2f4f7;
  font-size: 13px;
  color: #344054;
  vertical-align: middle;
}

.campanha-listagem__row:last-child td {
  border-bottom: none;
}

.campanha-listagem__row:hover {
  background: #f9fafb;
}

.col-id { width: 60px; text-align: center; }
.col-desc { }
.col-status { width: 100px; text-align: center; }
.col-data { width: 100px; text-align: center; }
.col-tipo { width: 100px; text-align: center; }
.col-validacao { width: 110px; text-align: center; }
.col-origem { width: 80px; text-align: center; }
.col-canais { width: 90px; text-align: center; }
.col-acoes { width: 120px; text-align: center; }

.campanha-listagem__desc {
  font-weight: 500;
  color: #1d2939;
}

.campanha-listagem__tipo-label,
.campanha-listagem__validacao-label {
  font-size: 12px;
  color: #555;
}

/* ===== ORIGEM BADGE ===== */
.origem-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
}

.origem-badge--erp {
  background: #edf8f9;
  color: #2298a3;
  border: 1px solid #c5e6e9;
}

.origem-badge--verso {
  background: #f2f2f8;
  color: #3a3e81;
  border: 1px solid #dddce9;
}

/* ===== CANAL ICONS ===== */
.campanha-listagem__canais {
  display: flex;
  gap: 6px;
  justify-content: center;
}

.canal-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #edf8f9;
  color: #2298a3;
  border: 1px solid #c5e6e9;
  cursor: default;
}

/* ===== EMPTY STATE ===== */
.campanha-listagem__empty {
  text-align: center;
  padding: 48px 20px !important;
  color: #98a2b3;
}

.campanha-listagem__empty-icon {
  color: #d0d5dd;
  margin-bottom: 8px;
}

.campanha-listagem__empty p {
  font-size: 13px;
  margin: 0;
}

/* ===== PAGINATION ===== */
.campanha-listagem__pagination {
  display: flex;
  justify-content: center;
}
</style>
